import React, {createRef, FC} from "react"
import Dropzone from "react-dropzone"
import Icon, {IconTypes} from "@irati/ui/src/Icon"
import styled, {css, DefaultTheme} from "styled-components"
import Flex from "@irati/ui/src/Flex"
import {Label} from "@irati/ui/src/Typography"
import {FormattedMessage} from "@irati/utils/lang"
import {useTheme} from "@irati/ui/src/global/useTheme"
import Button from "@irati/ui/src/Button"
import { FileStatus} from "@irati/types"
import Spinner from "@irati/ui/src/Loading/Spinner"
import {useCultureHubStore} from "../../../stores/hooks"
import {SelectedTab} from "./types"

interface Props {}

const maxSize = 10485760 // 10MB in binary
const dropzoneRef = createRef()

const UploadFile: FC<Props> = () => {
  const {state, dispatch} = useCultureHubStore()
  const theme = useTheme()

  const openDialog = () => {
    if (dropzoneRef.current) {
      dropzoneRef.current.open()
    }
  }

  const isValidPDF = async (file: File) => {
    try {
      const buffer = await file.slice(0, 5).arrayBuffer()
      const header = new Uint8Array(buffer)
      const pdfSignature = [0x25, 0x50, 0x44, 0x46, 0x2d] // "%PDF-"

      return pdfSignature.every((byte, index) => byte === header[index])
    } catch (error) {
      return false
    }
  }

  const handleDrop = async (acceptedFile: [File], rejectedFile: [File]) => {
    dispatch({
      type: "SET_UPLOAD_ERROR_MSG",
      payload: {uploadErrorMsg: ""}
    })

    if (rejectedFile.length > 0) {
      if (rejectedFile[0].type !== "application/pdf" && rejectedFile[0].size > maxSize) {
        dispatch({
          type: "SET_UPLOAD_ERROR_MSG",
          payload: {uploadErrorMsg: "culturehub.companyNews.modal.uploadFile.errors.fileTypeAndSize"}
        })
      } else if (rejectedFile[0].type !== "application/pdf") {
        dispatch({
          type: "SET_UPLOAD_ERROR_MSG",
          payload: {uploadErrorMsg: "culturehub.companyNews.modal.uploadFile.errors.fileType"}
        })
      } else if (rejectedFile[0].size > maxSize) {
        dispatch({
          type: "SET_UPLOAD_ERROR_MSG",
          payload: {uploadErrorMsg: "culturehub.companyNews.modal.uploadFile.errors.fileSize"}
        })
      }
      return
    }

    if (!acceptedFile[0]) return

    const validPDF = await isValidPDF(acceptedFile[0])
    if (validPDF) {
      dispatch({type: "ACCEPTED_FILE", payload: {acceptedFile: acceptedFile[0]}})
      dispatch({type: "ACCEPTED_FILE_VISUAL", payload: {acceptedFileVisual: acceptedFile[0]}})
      dispatch({
        type: "SET_MODAL_STATE",
        payload: {modalState: "AcceptTerms"}
      })
    } else {
      dispatch({
        type: "SET_UPLOAD_ERROR_MSG",
        payload: {uploadErrorMsg: "culturehub.companyNews.modal.uploadFile.errors.fileType"}
      })
    }
  }

  const acceptedFile = !state.uploadErrorMsg && state.acceptedFileVisual
  const rejectedFile = !state.uploadErrorMsg && !state.acceptedFileVisual
  const pendingState = acceptedFile && state.uploadStatus !== FileStatus.StatusReady
  const successState = acceptedFile && state.uploadStatus === FileStatus.StatusReady
  const initialState = rejectedFile && state.currentTab !== SelectedTab.EDIT
  const editState = rejectedFile && state.currentTab === SelectedTab.EDIT

  return (
    <StyledDropzone
      accept="application/pdf"
      maxSize={maxSize}
      onDrop={handleDrop}
      isValid={state.isPDFValid}
      isError={state.uploadErrorMsg}
      isSuccess={!!state.acceptedFileVisual && state.uploadStatus === FileStatus.StatusReady}
      isEdit={state.currentTab === SelectedTab.EDIT}>
      {state.uploadErrorMsg && (
        <Flex direction="column" alignItems="center">
          <Icon icon={IconTypes.circleCrossFill} size={32} color={theme.colors.utility.error} />
          <Label textAlign="center" marginBottom={1.5}>
            <FormattedMessage id={state.uploadErrorMsg} />
          </Label>
          <Button icon={IconTypes.document} small primary inverted onClick={openDialog}>
            <FormattedMessage id="culturehub.companyNews.modal.uploadFile.errorState.cta.title" />
          </Button>
        </Flex>
      )}

      {successState && (
        <Flex direction="column" alignItems="center">
          <Icon icon={IconTypes.circleTickFill} size={32} />
          <Label textAlign="center" marginBottom={1.5}>
            <FormattedMessage
              id="culturehub.companyNews.modal.uploadFile.successState.label.title"
              values={{fileName: <Label weight="bold">{state.uploadName}</Label>}}
            />
          </Label>
          <Button icon={IconTypes.document} small primary inverted onClick={openDialog}>
            <FormattedMessage id="culturehub.companyNews.modal.uploadFile.successState.cta.title" />
          </Button>
        </Flex>
      )}

      {pendingState && (
        <Flex direction="column" alignItems="center">
          <Flex>
            <Spinner size="sm" />
          </Flex>
          <Label textAlign="center">
            <FormattedMessage
              id="culturehub.companyNews.modal.uploadFile.pendingState.label.title"
              values={{fileName: <Label weight="bold">{state.uploadName}</Label>}}
            />
          </Label>
        </Flex>
      )}

      {initialState && (
        <Flex gap={4} alignItems="center">
          <Icon icon={IconTypes.uploadReport} size={48} color={theme.colors.greys.grey20} />
          <Flex direction="column" flex="1" alignItems="center">
            <Label weight="semibold">
              <FormattedMessage id="culturehub.companyNews.modal.uploadFile.initialState.topLabel" />
            </Label>
            <Label>
              <FormattedMessage id="culturehub.companyNews.modal.uploadFile.initialState.bottomLabel" />
            </Label>
          </Flex>
        </Flex>
      )}

      {editState && (
        <Flex direction="column" alignItems="center">
          <Button icon={IconTypes.document} small primary inverted onClick={openDialog}>
            <FormattedMessage id="culturehub.companyNews.modal.uploadFile.successState.cta.title" />
          </Button>
        </Flex>
      )}
    </StyledDropzone>
  )
}

function getBackgroundColor(theme: DefaultTheme, isError: string, isSuccess = false, isEdit = false) {
  if (isError) {
    return theme.colors.utility.bgError
  } else if (isSuccess) {
    return theme.colors.utility.bgSuccess
  } else if (isEdit) {
    return theme.colors.primary1_50
  }
  return "transparent"
}

function getBorderColor(theme: DefaultTheme, isError: string, isSuccess = false, isEdit = false, isValid = false) {
  if (isError || isValid) {
    return theme.colors.utility.error
  } else if (isSuccess) {
    return theme.colors.utility.success
  } else if (isEdit) {
    return theme.colors.primary1_100
  }
  return theme.colors.greys.grey20
}

const StyledDropzone = styled(Dropzone)`
  ${({theme, isError, isSuccess, isEdit, isValid}) => css`
    display: flex;
    flex-direction: column;
    justify-content: center;
    border-radius: ${theme.border.radius.medium};
    border-width: 2px;
    border-style: dashed;
    border-color: ${getBorderColor(theme, isError, isSuccess, isEdit, isValid)};
    background-color: ${getBackgroundColor(theme, isError, isSuccess, isEdit)};
    padding: ${theme.spacing.oneAndHalf} ${theme.spacing.three};
    margin: 10px 0 12px 0;
    cursor: pointer;
  `}
`

export default UploadFile
